import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import toast from 'react-hot-toast'
import { apiFetch } from '../lib/api'

export interface TradingRecord {
  id: number
  symbol: string
  name: string
  port_api_type: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold'
  action: 'BUY' | 'SELL'
  shares: number
  price: number
  currency: 'USD' | 'THB'
  exchange_rate?: number
  total_amount: number
  fees?: number
  notes?: string
  broker?: string
  created_at: string
  portfolio_id?: number | null
}

export interface PnLSummary {
  symbol: string
  name: string
  port_api_type: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold'
  sector?: string | null
  total_bought_shares: number
  total_sold_shares: number
  remaining_shares: number
  total_cost: number
  total_revenue: number
  realized_pnl: number
  unrealized_pnl: number
  total_pnl: number
  avg_buy_price: number
  avg_sell_price: number
  current_price?: number
  currency: 'USD' | 'THB'
  portfolio_id?: number | null
  portfolio_name?: string | null
  portfolio_icon?: string | null
}

interface TradingHistoryState {
  tradingHistory: TradingRecord[]
  pnlSummary: PnLSummary[]
  loading: boolean
  loadingHistory: boolean
  tradingHistoryLoaded: boolean
  loadingPnL: boolean
  pnlSummaryLoaded: boolean

  // Actions
  loadTradingHistory: (force?: boolean) => Promise<void>
  loadPnLSummary: (force?: boolean) => Promise<void>
  addTradingRecord: (
    record: Omit<TradingRecord, 'id' | 'created_at'> & { created_at?: string }
  ) => Promise<void>
  deleteTradingRecord: (id: number) => Promise<void>
  deleteTradingRecordsBySymbol: (symbol: string) => Promise<void>
  generatePnLReport: (period?: 'all' | '1y' | '6m' | '3m' | '1m') => Promise<PnLSummary[]>
}

export const useTradingHistoryStore = create<TradingHistoryState>()(
  devtools(
    (set, get) => ({
      tradingHistory: [],
      pnlSummary: [],
      loading: false,
      loadingHistory: false,
      tradingHistoryLoaded: false,
      loadingPnL: false,
      pnlSummaryLoaded: false,

      loadTradingHistory: async (force = false) => {
        const { loadingHistory, tradingHistoryLoaded } = get()
        if (!force && (tradingHistoryLoaded || loadingHistory)) return

        set({ loadingHistory: true, loading: true })
        try {
          const response = await apiFetch(`/api/trading-history`)
          if (!response.ok) throw new Error('Failed to fetch trading history')
          const data = await response.json()
          set({ tradingHistory: data, tradingHistoryLoaded: true })
        } catch (error) {
          console.error('Error loading trading history:', error)
          toast.error('Failed to load trading history')
        } finally {
          set({ loadingHistory: false, loading: false })
        }
      },

      loadPnLSummary: async (force = false) => {
        const { loadingPnL, pnlSummaryLoaded } = get()
        if (!force && (pnlSummaryLoaded || loadingPnL)) return

        set({ loadingPnL: true })
        try {
          const response = await apiFetch(`/api/pnl-summary`)
          if (!response.ok) throw new Error('Failed to fetch P&L summary')
          const data = await response.json()
          set({ pnlSummary: data, pnlSummaryLoaded: true })
        } catch (error) {
          console.error('Error loading P&L summary:', error)
          toast.error('Failed to load P&L summary')
        } finally {
          set({ loadingPnL: false })
        }
      },

      addTradingRecord: async (record) => {
        try {
          const response = await apiFetch(`/api/trading-history`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(record)
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to add trading record')
          }

          await Promise.all([get().loadTradingHistory(true), get().loadPnLSummary(true)])
          toast.success('Trading record added successfully')
        } catch (error: any) {
          console.error('Error adding trading record:', error)
          toast.error(error.message || 'Failed to add trading record')
        }
      },

      deleteTradingRecord: async (id) => {
        try {
          const response = await apiFetch(`/api/trading-history/${id}`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to delete trading record')
          }

          await get().loadTradingHistory(true)
          toast.success('Trading record deleted')
        } catch (error: any) {
          console.error('Error deleting trading record:', error)
          toast.error(error.message || 'Failed to delete trading record')
        }
      },

      deleteTradingRecordsBySymbol: async (symbol) => {
        try {
          const response = await apiFetch(`/api/trading-history/symbol/${symbol}`, {
            method: 'DELETE'
          })

          if (!response.ok) {
            const error = await response.json()
            throw new Error(error.error || 'Failed to delete trading records')
          }

          await Promise.all([get().loadTradingHistory(true), get().loadPnLSummary(true)])
          toast.success('All trading records for this stock deleted')
        } catch (error: any) {
          console.error('Error deleting trading records by symbol:', error)
          toast.error(error.message || 'Failed to delete trading records')
        }
      },

      generatePnLReport: async (period = 'all') => {
        try {
          const response = await apiFetch(`/api/pnl-report?period=${period}`)
          if (!response.ok) throw new Error('Failed to generate P&L report')
          const data = await response.json()
          return data
        } catch (error) {
          console.error('Error generating P&L report:', error)
          toast.error('Failed to generate P&L report')
          return []
        }
      }
    }),
    {
      name: 'trading-history-store'
    }
  )
)
